import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { RTEToolbarButton } from '../components/StyledTextToolbarButton';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Popover } from 'foundations-components/transitional/components/Popover';
import identity from 'transmute/identity';
import useDebouncedEditorStateContext from '../contexts/useDebouncedEditorStateContext';
import { FocusPlugin } from '../plugins/FocusPlugin';
import { LinkPlugin } from '../plugins/link/LinkPlugin';
import { convertToPlaintextState } from '../utils/plaintextUtils';
import { makeLinkNode } from '../plugins/link/LinkUtils';
import { Tracker } from '../../utils/usageTracker';
import { INSERT_LINK } from 'rich-text-lib/constants/usageTracking';
import { PM_LINK } from '../constants/events';
import { encodeUrl, ensureUrlHasProtocol } from '../../draft-transplant/utils/libUtils';
import LinkForm from '../../draft-transplant/components/LinkForm';
import { EditorConfigContext } from '../contexts/EditorConfigContext';
import { LinkIcon } from 'foundations-assets/icons/components/LinkIcon';
import { callIfPossible } from 'foundations-components/transitional/utils/functions';

/** Configuration for the Link capability (hyperlink insertion and editing) */
import { jsx as _jsx } from "react/jsx-runtime";
function LinkPopover(props) {
  const {
    initialText,
    setPopoverOpen,
    usePlaintextMode,
    plaintextOptions
  } = props;
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const [linkText, setLinkText] = useState(initialText);
  const [isTargetBlank, setTargetBlank] = useState(true);
  const [linkUrl, setLinkUrl] = useState('');
  const {
    LINK: linkConfig
  } = useContext(EditorConfigContext);
  const {
    noFollow = false
  } = linkConfig || {};
  const onLinkChange = useCallback(params => {
    if (params.url !== undefined) setLinkUrl(params.url);
    if (params.text !== undefined) setLinkText(params.text);
    if (params.isTargetBlank !== undefined) setTargetBlank(params.isTargetBlank);
  }, [setLinkUrl, setLinkText, setTargetBlank]);
  const onSave = useCallback(() => {
    const tr = editorState.tr;
    const urlWithProtocol = ensureUrlHasProtocol(linkUrl);
    const encodedUrl = encodeUrl(urlWithProtocol);
    const linkNode = makeLinkNode(editorState.schema, encodedUrl, linkText, isTargetBlank, noFollow, editorState.selection.$from.marks());
    tr.replaceRangeWith(editorState.selection.from, editorState.selection.to, linkNode);
    tr.setMeta(FocusPlugin, {
      focusView: true,
      focusViewPos: tr.mapping.map(editorState.selection.to)
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(editorState.apply(tr), plaintextOptions) : editorState.apply(tr));
    setPopoverOpen(false);
    Tracker.track(PM_LINK, {
      action: INSERT_LINK,
      externalLink: isTargetBlank,
      isImage: false,
      noFollow
    });
  }, [editorState, setEditorState, linkText, linkUrl, isTargetBlank, setPopoverOpen, usePlaintextMode, plaintextOptions, noFollow]);
  const onCancel = useCallback(() => {
    setPopoverOpen(false);
  }, [setPopoverOpen]);
  return /*#__PURE__*/_jsx(LinkForm, {
    isNew: true,
    text: linkText,
    url: linkUrl,
    isTargetBlank: isTargetBlank,
    isNoFollow: !!noFollow,
    onChange: onLinkChange,
    onConfirm: onSave,
    onCancel: onCancel,
    onRemove: identity,
    hideNoFollowConfiguration: true
  });
}
export default function InsertLink({
  usePlaintextMode,
  onOpenPopover,
  onClosePopover,
  popoverPlacement
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const linkPluginMeta = useMemo(() => LinkPlugin.getState(editorState), [editorState]);
  const {
    keyComboFired
  } = linkPluginMeta || {};
  // Prevents multiple calls to onOpenPopover and onClosePopover
  const prevIsPopoverOpen = usePrevious(isPopoverOpen);
  useEffect(() => {
    if (isPopoverOpen && !prevIsPopoverOpen) {
      callIfPossible(onOpenPopover);
    } else if (!isPopoverOpen && prevIsPopoverOpen) {
      callIfPossible(onClosePopover);
    }
  }, [isPopoverOpen, prevIsPopoverOpen, onOpenPopover, onClosePopover]);
  useEffect(() => {
    if (keyComboFired && !isPopoverOpen) {
      setLinkText(editorState.doc.textBetween(editorState.selection.from, editorState.selection.to));
      setPopoverOpen(!isPopoverOpen);

      // reset KeyCombo state to avoid accidental retriggering of this event
      setEditorState(editorState.apply(editorState.tr.setMeta(LinkPlugin, {
        keyComboFired: false
      })));
    }
  }, [setPopoverOpen, setEditorState, setLinkText, editorState, isPopoverOpen, keyComboFired]);
  const onButtonClick = useCallback(() => {
    setLinkText(editorState.doc.textBetween(editorState.selection.from, editorState.selection.to));
    setPopoverOpen(!isPopoverOpen);
  }, [setPopoverOpen, setLinkText, editorState, isPopoverOpen]);
  const onOpenChange = useCallback(({
    target: {
      value: evtVal
    }
  }) => {
    setPopoverOpen(evtVal);
  }, [setPopoverOpen]);
  const linkPopover = useMemo(() => {
    return () => /*#__PURE__*/_jsx(LinkPopover, {
      initialText: linkText,
      setPopoverOpen: setPopoverOpen,
      usePlaintextMode: usePlaintextMode
    });
  }, [linkText, setPopoverOpen, usePlaintextMode]);
  return /*#__PURE__*/_jsx(Popover, {
    open: isPopoverOpen,
    onOpenChange: onOpenChange,
    Content: linkPopover,
    placement: popoverPlacement,
    children: /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.tooltips.insertLink"
      }),
      open: isPopoverOpen ? false : undefined,
      children: /*#__PURE__*/_jsx(RTEToolbarButton, {
        onClick: onButtonClick,
        selected: isPopoverOpen,
        "aria-label": I18n.text('hubspot-prosemirror.tooltips.insertLink'),
        children: /*#__PURE__*/_jsx(TextToolbarIcon, {
          "data-test-id": "link-button",
          icon: LinkIcon
        })
      })
    })
  });
}